import { toast } from "sonner";

import { keys } from "@/hooks/queries";
import { api, queryClient, type NodeOut } from "@/lib/api";
import { notifyError } from "@/lib/errors";
import { useTabs } from "@/stores/tabs";

/** Create a blank markdown NOTE and open it in the rich editor — the markdown
 * counterpart to the blank canvas note. The note is vault-mirrored on creation,
 * so it shows under Markdown/ in the explorer immediately. */
export async function createMarkdownNote(): Promise<void> {
  try {
    const node = (await api.createNode({
      type: "note",
      label: "Untitled note",
      text: "",
    } as never)) as unknown as NodeOut;
    // Validate the id BEFORE opening a panel: an unchecked cast means a shape
    // drift (no id, non-string id) would open a `markdown:undefined` ghost
    // panel that can never load and persists across reloads. Surface it as an
    // honest error and abort instead.
    if (typeof node?.id !== "string" || !node.id) {
      notifyError(new Error("Note creation returned an unexpected response."));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: keys.overview });
    await queryClient.invalidateQueries({ queryKey: keys.vault });
    useTabs.getState().openMarkdown(node.id, node.label || "Untitled note");
  } catch (e) {
    toast.error("Could not create note", { description: String(e) });
  }
}

/** The one fixed, non-renamable note: an always-there landing page for the
 * vault, filed at the TOP of the Markdown folder (no `folder`, so the mirror
 * writes it as `Markdown/<label>.md` rather than into a subfolder).
 *
 * The label is load-bearing, not decorative — it is how the note is found again
 * (`/api/nodes/resolve` resolves wikilinks and this alike, by label). Renaming
 * it would orphan the rail button, so the editor locks the title bar and skips
 * its H1→filename reconcile for exactly this label. */
export const ATLAS_MASTER_LABEL = "Atlas Master File";

/** Open the Atlas Master File, creating it the first time. Idempotent in the
 * same way as the daily note: resolve by label first so a second press reopens
 * the existing note rather than making a duplicate. */
export async function openAtlasMasterFile(): Promise<void> {
  try {
    try {
      const existing = await api.request<NodeOut>(
        "GET",
        `/api/nodes/resolve?label=${encodeURIComponent(ATLAS_MASTER_LABEL)}`,
      );
      if (existing?.id && existing.type === "note") {
        useTabs.getState().openMarkdown(existing.id, ATLAS_MASTER_LABEL);
        return;
      }
    } catch {
      /* not created yet — fall through */
    }
    const node = (await api.createNode({
      type: "note",
      label: ATLAS_MASTER_LABEL,
      text: `# ${ATLAS_MASTER_LABEL}\n\n`,
    } as never)) as unknown as NodeOut;
    if (typeof node?.id !== "string" || !node.id) {
      notifyError(new Error("Could not open the Atlas Master File."));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: keys.overview });
    await queryClient.invalidateQueries({ queryKey: keys.vault });
    useTabs.getState().openMarkdown(node.id, ATLAS_MASTER_LABEL);
  } catch (e) {
    toast.error("Could not open the Atlas Master File", { description: String(e) });
  }
}

/** Today's date as the daily note's label (the user's local calendar day). */
export function dailyNoteLabel(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Open today's daily note — a markdown note named for the date, filed under
 * Markdown/daily notes/ in the vault. Idempotent: if today's note already
 * exists it is OPENED (resolved by label), never duplicated; otherwise it is
 * created with the folder attached so the mirror + explorer file it right. */
export async function openDailyNote(): Promise<void> {
  const label = dailyNoteLabel();
  try {
    // Resolve-by-label first so a second press the same day reopens the note.
    try {
      const existing = await api.request<NodeOut>(
        "GET",
        `/api/nodes/resolve?label=${encodeURIComponent(label)}`,
      );
      if (existing?.id && existing.type === "note") {
        useTabs.getState().openMarkdown(existing.id, existing.label || label);
        return;
      }
    } catch {
      /* no note with today's label yet — create it below */
    }
    const node = (await api.createNode({
      type: "note",
      label,
      text: `# ${label}\n\n`,
      folder: "daily notes",
    } as never)) as unknown as NodeOut;
    if (typeof node?.id !== "string" || !node.id) {
      notifyError(new Error("Daily-note creation returned an unexpected response."));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: keys.overview });
    await queryClient.invalidateQueries({ queryKey: keys.vault });
    useTabs.getState().openMarkdown(node.id, node.label || label);
  } catch (e) {
    toast.error("Could not open the daily note", { description: String(e) });
  }
}
